import { Badge, Button, Card, Flex, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { noticeFactsOf, noticeTitle } from '@/shared/domain/noticeFacts';
import { notificationRelationKey } from '@/shared/domain/notificationRelations';
import { platformReasonKey } from '@/shared/domain/reasonVocabulary';
import { relativeWhen } from '@/shared/domain/relativeWhen';
import { steamCodeSuffix } from '@/shared/domain/steamDetail';
import { NoteText } from '@/shared/ui/primitives';
import type { Notice } from './api';

/**
 * 一条通知行。
 *
 * **行是证据、帧是提示**（ADR 0005 D6）：这行显示的是「哪件事、什么档位、
 * 你与它什么关系、什么时候」，**不是**业务现值 ⇒ 业务详情由用户点进对应
 * 页面去看（行里没有 `payload` 快照，见 `api.ts::Notice`）。
 *
 * ## 与浮层同一套命名（2026-10-24 统一风格）

 * 标题 = **事件名**（`noticeTitle`：帧 + 分档，与浮层 `SseToasts` 同源同派生
 * ——行读面的 username/stage/action/code 列就是事实槽位）⇒ 同一条通知在
 * 浮层说「保活失败」、在这里也说「保活失败」，不再一个说「保活失败」一个
 * 说「登录失败」。旧的「触发操作」行随之退役（事件名已含操作档，再列一遍
 * 类别名只会自相矛盾）。
 */
export function NoticeRow({
  row,
  marking,
  onMarkRead,
}: {
  row: Notice;
  /** 这一行正在提交「标已读」——按钮转圈，别让人重复点。 */
  marking: boolean;
  onMarkRead: (id: string) => void;
}) {
  const { t } = useTranslation();
  const unread = row.read_at === null;
  // 关系档位（waiter/anchor/extra/related）**不是业务失败码** ⇒ 查关系档位表，
  // 不查 `reasonVocabulary`（那张表里没有这四个值 ⇒ 查了必然落空）。
  // ⚠️ 关系档位只在**列表**里展示（逐行有上下文、可扫）；浮层按用户裁定
  // 2026-10-24 不指向「你」。
  const relationKey =
    row.reason === null || row.reason === undefined ? null : notificationRelationKey(row.reason);
  // 原始 Steam 码值是协议事实：不翻译、弱化显示（次要色 + 小号）。
  // ⚠️ 读面**不返回 `payload`** ⇒ 今天这行取不到码值；码值在铃铛帧的
  // `data` 键里（浮层那条链路读得到）。这里仍按「行里有就显示」写。
  const code = steamCodeSuffix(row);
  const title = noticeTitle(row.frame, noticeFactsOf(row));
  const when = relativeWhen(row.created_at, t);
  return (
    <Card size="small">
      <Flex vertical gap={4}>
        <Flex align="center" justify="space-between" gap="small" wrap>
          <Flex align="center" gap={6} wrap>
            {unread ? <Badge status="processing" /> : null}
            <Typography.Text strong={unread}>{t(title.key)}</Typography.Text>
            {row.importance === 'IMPORTANT' ? (
              <Tag color="red">{t('notifications.important')}</Tag>
            ) : null}
            {relationKey === null ? null : <Tag>{t(relationKey)}</Tag>}
          </Flex>
          {unread ? (
            <Button size="small" type="link" loading={marking} onClick={() => onMarkRead(row.id)}>
              {t('notifications.markRead')}
            </Button>
          ) : null}
        </Flex>
        {/* 主体行 + 事实行——与浮层同一套（轮廓统一 2026-10-24）。
            ⚠️ 主体行第一：账号域事件的主体是**账号**；`cdkey.bound` 的主体是
            **卡**（CDKey 行第一，账号退为事实行——用户裁定 2026-10-24）。
            失败码与浮层同口径：词表查得到印短语，查不到印原样。 */}
        {row.cdkey === null || row.cdkey === undefined ? null : (
          <Typography.Text type="secondary">
            {t('notifications.cdkeyCode', { cdkey: row.cdkey })}
          </Typography.Text>
        )}
        {row.username === null || row.username === undefined ? null : (
          <Typography.Text type="secondary">
            {t('notifications.subjectAccount', { name: row.username })}
          </Typography.Text>
        )}
        {row.order_no === null || row.order_no === undefined ? null : (
          <Typography.Text type="secondary">
            {t('events.notify.factOrder', { order: row.order_no })}
          </Typography.Text>
        )}
        {(() => {
          if (row.code === null || row.code === undefined) return null;
          const phrase = platformReasonKey(row.code);
          return (
            <Typography.Text type="secondary">
              {phrase === null ? t('events.notify.factCode', { code: row.code }) : t(phrase)}
            </Typography.Text>
          );
        })()}
        {/* 公告正文——**只有公告行有**（业务通知行的载荷是业务快照，不进读面）。
            没有正文的话这行只剩一个「系统公告」标题 ⇒ 用户看到一条读不懂的通知。 */}
        {row.message === null || row.message === undefined ? null : (
          <Typography.Text>{row.message}</Typography.Text>
        )}
        <Flex align="center" gap={6} wrap>
          {when === null ? null : <NoteText>{when}</NoteText>}
          {code === null ? null : <NoteText>{code}</NoteText>}
        </Flex>
      </Flex>
    </Card>
  );
}
